// listItem.ts
import type { ActivityGroupStatus, IScheduleProfession, ISchedulerDependency, ISchedulerTask } from '../../interfaces/IScheduler.js';
import type { IIssue } from '../../../trusstor/index.js';
import {
  onTimeRiskForActivity, onTimeRiskForIssue,
  safetyRiskForActivity, safetyRiskForIssue,
  type IRiskScore, type ISafetyRisk,
} from './risk.js';
import { dateTimeFormat } from '../../utils/dateTimeFormat.js';

export const toDay = (
  d: string | Date | null | undefined,
  tz: string,
): string | undefined => {
  if (d == null) return undefined;
  // A bare 'YYYY-MM-DD' is already a calendar day — pass it through.
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  // Full ISO timestamp or Date → the PROJECT's calendar day (consistent with
  // projectToday()). Never slice an ISO timestamp: that yields the UTC day, a
  // day behind for values at local midnight in any zone ahead of UTC (e.g.
  // 2026-06-25T15:00Z is Jun 26 on a UTC+9 site).
  return dateTimeFormat('en-CA', { timeZone: tz }).format(new Date(d));
};

export type ListItemKind = 'activity' | 'issue';

/**
 * The unified list's item-key convention (`act:<taskId>` / `iss:<issueId>`).
 * This module owns the format; every producer or matcher of these keys
 * (list items, the alerts card's drawer, the alert deep-link filter) must go
 * through these builders so the two prefixes can never drift apart.
 */
export const activityItemKey = (taskId: string): string => `act:${taskId}`;
export const issueItemKey = (issueId: string): string => `iss:${issueId}`;

export interface IRelatedTask {
  id: string;
  name: string;
  start: string;
  end: string;
  progressPercent: number;
  /** Whether the neighbour is a committed activity. An uncommitted one is
   *  still the plan's — it has no row on the Activities page, so a surface
   *  listing it says so rather than offering to open it. */
  committed: boolean;
}

/** A task's dependency neighbours — shared by the list item and the risk badge. */
export interface IRelatedTasks {
  predecessors: IRelatedTask[];
  successors: IRelatedTask[];
}

const toRelated = (task: ISchedulerTask): IRelatedTask => ({
  id: task.id,
  name: task.name,
  start: task.start,
  end: task.end,
  progressPercent: task.progressPercent,
  committed: task.committed,
});

/**
 * Every task's dependency neighbours, indexed in a single O(D) pass over the
 * links — what each list item carries and its risk badge counts as `hasDeps`.
 * Neighbours resolve against the whole `plan` as well as `tasks` (a task in
 * both reads as `tasks` holds it): the feed holds only the committed tasks,
 * and a committed activity's predecessor is still its predecessor before it
 * is committed. A link to a task in neither is skipped.
 */
export function relatedTasksIndex(
  tasks: readonly ISchedulerTask[],
  deps: readonly Pick<ISchedulerDependency, 'predecessorId' | 'successorId'>[],
  plan: readonly ISchedulerTask[] = [],
): (taskId: string) => IRelatedTasks {
  const taskById = new Map([...plan, ...tasks].map((task) => [task.id, task] as const));
  const predecessorIdsOf = new Map<string, string[]>();
  const successorIdsOf = new Map<string, string[]>();
  const link = (index: Map<string, string[]>, from: string, to: string) => {
    const ids = index.get(from);
    if (ids) ids.push(to);
    else index.set(from, [to]);
  };
  for (const dep of deps) {
    link(predecessorIdsOf, dep.successorId, dep.predecessorId);
    link(successorIdsOf, dep.predecessorId, dep.successorId);
  }
  const related = (ids: string[] | undefined): IRelatedTask[] =>
    (ids ?? []).flatMap((id) => {
      const task = taskById.get(id);
      return task ? [toRelated(task)] : [];
    });
  return (taskId) => ({ predecessors: related(predecessorIdsOf.get(taskId)), successors: related(successorIdsOf.get(taskId)) });
}

export interface IListItem {
  key: string;
  kind: ListItemKind;
  title: string;
  start?: string;
  end?: string;
  date: string;
  status: string;
  /**
   * The Trusstor activities-group status of a COMMITTED activity, joined by the
   * Activities feed. Absent for issues, for uncommitted tasks and for an
   * `administrative` activity, which has no Trusstor side to report.
   */
  activityStatus?: ActivityGroupStatus;
  /**
   * The item's trade — what the profession filter keys on (`tradeId`). An
   * ACTIVITY's is its profession snapshot, rendered as the shared
   * `ProfessionPill`; an ISSUE's is built from its minimized profession, which
   * carries no specialty, contractor or colour (`issueProfession`). Absent for
   * an unassigned activity or issue.
   */
  profession?: IScheduleProfession;
  /** Free-text assignee — the person a Genba ISSUE is assigned to. Activities use
   *  the structured `profession` snapshot instead. */
  assigneeName?: string;
  progressPercent?: number;
  onTimeRisk: IRiskScore;
  safety: ISafetyRisk;
  isCritical: boolean;
  /** CPM critical-path membership (activities only) — drives the gold title tag. */
  isOnCriticalPath: boolean;
  /** Milestone activity — drives the diamond marker and the Milestones filter.
   *  Always false for issues. */
  isMilestone: boolean;
  isSafety: boolean;
  isRejected: boolean;
  isOverdue: boolean;
  commentCount: number;
  raw: { activity?: ISchedulerTask; issue?: IIssue };
  relatedTasks?: IRelatedTasks;
}

export function activityToListItem(
  task: ISchedulerTask,
  ctx: {
    today: string;
    isCritical: boolean;
    relatedTasks?: IRelatedTasks;
    /** Trusstor's status for this task's activity, when the caller joined it. */
    activityStatus?: ActivityGroupStatus;
  },
): IListItem {
  const hasDeps =
    (ctx.relatedTasks?.predecessors.length ?? 0) > 0 ||
    (ctx.relatedTasks?.successors.length ?? 0) > 0;
  const onTimeRisk = onTimeRiskForActivity({
    start: task.start, end: task.end, progressPercent: task.progressPercent,
    status: task.status, today: ctx.today, isCritical: ctx.isCritical, hasDeps,
  });
  const safety = safetyRiskForActivity({ title: task.name });
  const isOverdue =
    task.status !== 'done' && ctx.activityStatus !== 'complete' && task.progressPercent < 100 &&
    Date.parse(ctx.today) > Date.parse(task.end);
  return {
    key: activityItemKey(task.id),
    kind: 'activity',
    title: task.name,
    start: task.start,
    end: task.end,
    date: task.start,
    status: task.status,
    ...(ctx.activityStatus ? { activityStatus: ctx.activityStatus } : {}),
    profession: task.profession,
    progressPercent: task.progressPercent,
    onTimeRisk,
    safety,
    // Activities have no "critical" flag of their own. ctx.isCritical is the CPM
    // critical-path membership and feeds ONLY the on-time risk bump above — it is
    // intentionally not surfaced as a pin/filter/halo. The critical flag belongs
    // to issues (manual priority).
    isCritical: false,
    isOnCriticalPath: ctx.isCritical,
    isMilestone: task.isMilestone,
    isSafety: safety.isFlag,
    isRejected: false,
    isOverdue,
    // Activities have no comment thread in this surface — the drawer only renders
    // the chat for issues — so there are never unread comments to count.
    commentCount: 0,
    raw: { activity: task },
    relatedTasks: ctx.relatedTasks,
  };
}

/** An issue's minimized profession as the list's profession snapshot. It
 *  knows the trade and its group only — the rest stays empty, never guessed. */
function issueProfession(profession: NonNullable<IIssue['profession']>): IScheduleProfession {
  return {
    professionId: profession._id,
    tradeId: profession.tradeId,
    tradeGroup: profession.tradeGroup,
    specialty: null,
    contractor: null,
    backgroundColor: null,
  };
}

export function issueToListItem(issue: IIssue, ctx: { today: string; tz: string }): IListItem {
  const due = toDay(issue.dueDate, ctx.tz);
  const onTimeRisk = onTimeRiskForIssue({
    dueDate: due ?? null,
    status: issue.status,
    today: ctx.today,
    isCritical: issue.priority === 'critical',
  });
  const safety = safetyRiskForIssue({ isSafety: !!issue.isSafety });
  const isOverdue =
    issue.status !== 'completed' && !!due && Date.parse(ctx.today) > Date.parse(due);
  return {
    key: issueItemKey(issue._id),
    kind: 'issue',
    title: issue.description,
    end: due,
    date: due ?? toDay(issue.createDate, ctx.tz)!,
    status: issue.status,
    ...(issue.profession ? { profession: issueProfession(issue.profession) } : {}),
    assigneeName: issue.assignee?.name ?? issue.assignee?.username ?? undefined,
    onTimeRisk,
    safety,
    isCritical: issue.priority === 'critical',
    isOnCriticalPath: false,
    isMilestone: false,
    isSafety: !!issue.isSafety,
    isRejected: issue.status === 'rejected',
    isOverdue,
    commentCount: issue.comments?.length ?? 0,
    raw: { issue },
  };
}
